import { CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Chess, Square } from 'chess.js';
import { defer, delay, flatten, without } from 'lodash-es';
import { catchError, Observable, of, Subscription, throwError } from 'rxjs';
import { Ref } from '../../model/ref';
import { ActionService } from '../../service/action.service';
import { ConfigService } from '../../service/config.service';
import { Store } from '../../store/store';

export type PieceType = 'p' | 'n' | 'b' | 'r' | 'q' | 'k';
export type PieceColor = 'b' | 'w';

type Piece = { type: PieceType, color: PieceColor, square: Square, };
type AnimationState = { from: Square; to: Square; capture?: { square: Square; piece: Piece }; piece: Piece; boardState: (Piece | null)[], turnState: PieceColor, movesState: Square[] };

@Component({
  selector: 'app-chess',
  templateUrl: './chess.component.html',
  styleUrls: ['./chess.component.scss'],
  hostDirectives: [CdkDropListGroup],
  host: {
    'class': 'chess-board',
    '[class.flip]': 'flip()',
    '(window:resize)': 'onResize()',
  },
  imports: [CdkDropList, CdkDrag]
})
export class ChessComponent {
  config = inject(ConfigService);
  private actions = inject(ActionService);
  private store = inject(Store);
  private el = inject<ElementRef<HTMLDivElement>>(ElementRef);


  readonly refInput = input<Ref | undefined>(undefined, { alias: 'ref' });
  readonly textInput = input<string | undefined>('', { alias: 'text' });
  readonly whiteInput = input(true, { alias: 'white' });
  readonly ref = this.refInput;
  readonly text = linkedSignal(() => this.textInput());
  // TODO: Save in local storage
  readonly white = linkedSignal(() => this.whiteInput());
  readonly comment = output<string>();
  readonly copied = output<string>();

  readonly from = signal<Square | undefined>(undefined);
  readonly to = signal<Square | undefined>(undefined);
  chess = new Chess();
  private readonly displayState = signal<{
    pieces: (Piece | null)[];
    turn: PieceColor;
    moves: Square[];
    lastMoveTo?: Square;
  }>({ pieces: flatten(this.chess.board()), turn: 'w', moves: [] });
  readonly pieces = computed(() => this.displayState().pieces);
  readonly turn = computed(() => this.displayState().turn);
  readonly moves = linkedSignal(() => this.displayState().moves);
  readonly lastMoveTo = computed(() => this.displayState().lastMoveTo);
  // chess.js mutates in place, so model changes invalidate the cached history.
  private readonly historyVersion = signal(0);
  readonly history = computed(() => {
    this.historyVersion();
    return (this.fen ? this.fen + '\n\n' : '') + this.chess.history().join('  \n');
  });
  readonly writeAccess = signal(false);
  readonly translate = signal<string[]>([]);
  readonly animating = signal(false);
  animationQueue: AnimationState[] = [];
  readonly movingPiece = signal<{ piece: Piece; from: Square; to: Square } | undefined>(undefined);
  readonly capturedPiece = signal<{ piece: Piece; square: Square } | undefined>(undefined);
  readonly promotion = signal<{ from: Square; to: Square } | undefined>(undefined);
  readonly promotionPieces: Exclude<PieceType, 'p' | 'k'>[] = ['q', 'r', 'b', 'n'];
  readonly flip = signal(false);

  private resizeObserver = window.ResizeObserver && new ResizeObserver(() => this.onResize()) || undefined;
  private fen = '';
  private watch?: Subscription;
  private append$!: (value: string) => Observable<string>;
  private board?: string;
  private retry: string[] = [];

  constructor() {
    this.store.eventBus.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event.event === 'flip' && event.ref?.url === this.ref()?.url) {
        this.flip.set(true);
        delay(() => {
          this.flip.set(false);
          this.white.update(white => !white);
        }, 1000);
        defer(() => this.store.eventBus.fire('flip-done'));
      }
    });
    let first = true;
    let previousRef: Ref | undefined;
    effect(() => {
      const ref = this.refInput();
      const text = this.textInput();
      untracked(() => {
        const newRef = first || previousRef?.url !== ref?.url || previousRef?.origin !== ref?.origin;
        if (!ref || newRef) {
          this.watch?.unsubscribe();
          this.watch = undefined;
          if (newRef) this.board = undefined;
          if (ref || text != null) this.init();
        }
        first = false;
        previousRef = ref;
      });
    });
  }

  private readonly onInit = afterNextRender(() => {
    this.resizeObserver?.observe(this.el.nativeElement);
    this.onResize();
  });

  init() {
    this.reset(this.ref()?.comment || this.text());
    if (!this.watch && this.ref()) {
      const watch = this.actions.append(this.ref()!);
      this.append$ = watch.append$;
      this.watch = watch.updates$.pipe(
        catchError(err => {
          if (err.url) {
            // Game history rewritten
            alert($localize`Game History Rewritten!\n\nPlease Reload.`);
          }
          return of();
        }),
      ).subscribe(move => {
        try {
          this.chess.move(move);
          this.historyVersion.update(version => version + 1);
          this.check();
          if (this.retry.length) {
            if (this.retry[0] === move) {
              // TODO: also check move number?
              this.retry.shift();
              return;
            } else {
              this.retrySave();
            }
          }
          const boardState = flatten(this.chess.board());
          const turnState = this.chess.turn();
          const movesState = this.chess.moves({ verbose: true }).map(m => m.to);
          const moveObj = this.chess.history({ verbose: true }).pop();
          if (moveObj) {
            const capturedPieceInfo = moveObj.captured ? {
              square: moveObj.to,
              piece: { type: moveObj.captured, color: moveObj.color === 'w' ? 'b' : 'w', square: moveObj.to } as Piece
            } : undefined;
            this.queueAnimation({
              from: moveObj.from,
              to: moveObj.to,
              capture: capturedPieceInfo,
              piece: { type: moveObj.piece, color: moveObj.color, square: moveObj.to },
              boardState,
              turnState,
              movesState
            });
          }
        } catch (e) {
          this.clearErrors();
        }
      });
    }
  }

  private readonly onDestroy = inject(DestroyRef).onDestroy(() => {
    this.resizeObserver?.disconnect();
    this.watch?.unsubscribe();
  });

  clearErrors() {
    if (this.ref()) {
      this.actions.comment(this.history(), this.ref()!);
    } else {
      this.text.set(this.history());
    }
  }

  reset(board?: string) {
    if (this.board === board) return;
    this.board = board;
    this.chess.clear();
    this.fen = '';
    try {
      if (board) {
        const lines = board.trim().split('\n');
        this.chess.load(lines[0]);
        this.fen = this.chess.fen();
        lines.shift();
        for (const l of lines) {
          if (!l.trim()) continue;
          try {
            this.chess.move(l);
          } catch (e) {
            console.log(e);
          }
        }
      } else {
        this.chess.loadPgn('');
      }
    } catch (e) {
      try {
        this.chess.loadPgn(board || '');
      } catch (e) {
        console.error(e);
      }
    }
    this.render();
    if (!this.ref() || (this.ref()!.comment || '') !== this.history()) {
      this.clearErrors();
    }
  }

  onResize() {
    const dim = Math.floor(this.el.nativeElement.offsetWidth / 8);
    const fontSize = Math.floor(0.75 * dim);
    this.el.nativeElement.style.setProperty('--dim', dim + 'px');
    this.el.nativeElement.style.setProperty('--piece-size', fontSize + 'px');
  }

  setPieceSize() {
    const dim = Math.floor(this.el.nativeElement.offsetWidth / 8);
    const fontSize = Math.floor(0.75 * dim);
    document.body.style.setProperty('--drag-piece-size', fontSize + 'px');
  }

  drawPiece(p?: Piece | null) {
    if (p?.color === 'w') {
      switch (p.type) {
        case 'k': return $localize`♔`;
        case 'q': return $localize`♕`;
        case 'r': return $localize`♖`;
        case 'b': return $localize`♗`;
        case 'n': return $localize`♘`;
        case 'p': return $localize`♙`;
      }
    } else if (p?.color === 'b') {
      switch (p.type) {
        case 'k': return $localize`♚`;
        case 'q': return $localize`♛`;
        case 'r': return $localize`♜`;
        case 'b': return $localize`♝`;
        case 'n': return $localize`♞`;
        case 'p': return $localize`♟︎`;
      }
    }
    return ' ';
  }

  getIndex(place: string) {
    const col = place[0].toLowerCase().charCodeAt(0) - 'a'.charCodeAt(0);
    const row = 9 - parseInt(place[1]);
    return row * 8 + col;
  }

  getCoord(index: number): Square {
    const col = index % 8;
    const row = 8 - Math.floor(index / 8);
    return String.fromCharCode('a'.charCodeAt(0) + col) + row as Square;
  }

  color(index: number) {
    const col = (index % 8) % 2;
    const row = Math.floor(index / 8) % 2;
    return col === row ?  'light' : 'dark';
  }

  drop(event: CdkDragDrop<number, number, Piece>) {
    const from = this.getCoord(event.previousContainer.data);
    const to = this.getCoord(event.container.data);
    if (from === to) return;
    this.move(from, to);
  }

  move(from: Square, to: Square, promotion?: Exclude<PieceType, 'p' | 'k'>) {
    if (from === to) return;
    this.promotion.set(undefined);
    const isPromotion = !!this.chess.moves({ verbose: true }).find((move) => move.from === from && move.to === to && move.flags.includes('p'));
    if (isPromotion && !promotion) {
      this.promotion.set({ from, to });
      return;
    }
    const move = this.chess.move({ from, to, promotion: isPromotion ? promotion : undefined });
    if (move) {
      this.render();
      this.check();
      this.save(move.san);
    }
  }

  render() {
    this.historyVersion.update(version => version + 1);
    this.displayState.set({
      pieces: flatten(this.chess.board()),
      lastMoveTo: this.chess.history({ verbose: true }).pop()?.to,
      turn: this.chess.turn(),
      moves: this.chess.moves({ verbose: true }).map(m => m.to),
    });
  }

  check() {
    if (this.chess.isGameOver()) {
      defer(() => {
        if (this.chess.isCheckmate()) {
          alert($localize`Checkmate!`);
        } else if (this.chess.isStalemate()) {
          alert($localize`Stalemate!`);
        } else if (this.chess.isThreefoldRepetition()) {
          alert($localize`Threefold Repetition!`);
        } else if (this.chess.isInsufficientMaterial()) {
          alert($localize`Insufficient Material!`);
        } else if (this.chess.isDraw()) {
          alert($localize`Draw!`);
        } else {
          alert($localize`Game Over!`);
        }
      });
    }
    this.from.set(undefined);
    this.to.set(undefined);
  }

  save(move: string) {
    this.comment.emit(this.history());
    this.append$(move).pipe(
      catchError(err => {
        this.retry.push(move);
        delay(() => {
          if (this.retry.length) {
            this.clearErrors();
          }
        }, 1000);
        return throwError(() => err);
      }),
    ).subscribe();
  }

  retrySave() {
    if (!this.retry.length) return;
    const move = this.retry.shift()!;
    if (this.chess.moves().includes(move)) {
      this.append$(move).pipe(
        catchError(err => {
          this.retry.unshift(move);
          return throwError(() => err);
        }),
      ).subscribe();
    } else {
      this.retry.length = 0;
    }
  }

  promote(type: Exclude<PieceType, 'p' | 'k'>) {
    const promotion = this.promotion();
    if (!promotion) return;
    this.move(promotion.from, promotion.to, type);
  }

  cancelPromotion() {
    this.promotion.set(undefined);
  }

  clickSquare(index: number) {
    if (this.promotion()) return;
    const square = this.getCoord(index);
    const p = this.chess.get(square);
    if (this.from() === square) {
      this.from.set(undefined);
      this.moves.set(this.chess.moves({ verbose: true }).map(m => m.to));
    } else if (this.turn() === p?.color) {
      this.from.set(square);
      this.moves.set(this.chess.moves({ square, verbose: true }).map(m => m.to));
    } else if (this.from()) {
      this.to.set(square);
      this.move(this.from()!, square);
    }
  }

  private getMoveCoord(move: string, turn: PieceColor): Square {
    if (move === 'O-O') {
      return turn === 'w' ? 'g1' : 'g8';
    } else if (move === 'O-O-O') {
      return turn === 'w' ? 'c1' : 'c8';
    } else {
      return move.replace(/[^a-h1-8]/g, '') as Square;
    }
  }

  queueAnimation(state: AnimationState) {
    this.animationQueue.push(state);
    if (!this.animating()) {
      this.processAnimationQueue();
    }
  }

  processAnimationQueue() {
    if (this.animationQueue.length === 0) {
      this.animating.set(false);
      this.movingPiece.set(undefined);
      this.capturedPiece.set(undefined);
      this.render();
      this.check();
      return;
    }

    this.animating.set(true);
    const animation = this.animationQueue.shift()!;
    this.displayState.set({
      pieces: animation.boardState,
      turn: animation.turnState,
      moves: animation.movesState,
      lastMoveTo: animation.to,
    });
    this.movingPiece.set({ piece: animation.piece, from: animation.from, to: animation.to });
    if (animation.capture) {
      this.capturedPiece.set(animation.capture);
    }

    // Calculate coordinates for CSS animation
    const fromIndex = this.getIndex(animation.from);
    const toIndex = this.getIndex(animation.to);
    const fromCol = fromIndex % 8;
    const fromRow = Math.floor(fromIndex / 8);
    const toCol = toIndex % 8;
    const toRow = Math.floor(toIndex / 8);

    // Calculate deltas - the piece at destination needs to animate FROM source position
    // So we need the negative offset from destination back to source
    const xFrom = this.white() ? -(toCol - fromCol) : -(fromCol - toCol);
    const yFrom = this.white() ? -(toRow - fromRow) : -(fromRow - toRow);
    const xTo = 0;
    const yTo = 0;

    // Set CSS variables on the host element
    this.el.nativeElement.style.setProperty('--xFrom', xFrom.toString());
    this.el.nativeElement.style.setProperty('--yFrom', yFrom.toString());
    this.el.nativeElement.style.setProperty('--xTo', xTo.toString());
    this.el.nativeElement.style.setProperty('--yTo', yTo.toString());

    // Animate the piece moving to its destination with translation
    const movingPiece = animation.to;
    this.translate.update(translate => [...translate, movingPiece]);

    // Remove captured piece animation after it completes (delay + duration)
    // Capture animation: 1.0s delay + 0.8s animation = 1.8s total
    if (animation.capture) {
      delay(() => {
        this.capturedPiece.set(undefined);
      }, 1800);
    }

    // Remove animation after completion (wait for capture to finish if present)
    const totalDuration = animation.capture ? 1900 : 1600;
    delay(() => {
      this.translate.update(translate => without(translate, movingPiece));
      this.movingPiece.set(undefined);
      // capturedPiece already deleted above if it existed
      if (!animation.capture) {
        this.capturedPiece.set(undefined);
      }
      // Process next animation after current one completes
      this.processAnimationQueue();
    }, totalDuration);
  }
}
